import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ExternalLink, Search, Sparkles } from "lucide-react";
import { APPS } from "@/data/content";
import { Reveal } from "@/components/Reveal";

const StatusBadge = ({ status }) => {
  const map = {
    Live: "bg-emerald-100 text-emerald-800 border-emerald-300",
    Beta: "bg-amber-100 text-amber-800 border-amber-300",
    Coming: "bg-stone-100 text-stone-600 border-stone-300",
  };
  const cls = map[status] || map.Coming;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-1 text-[10px] font-medium uppercase tracking-[0.15em] border ${cls}`}>
      <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70" /> {status}
    </span>
  );
};

const AppCard = ({ app, index }) => (
  <Reveal delay={index * 60} className="bento-card group flex flex-col" data-testid={`app-card-${app.id}`}>
    <div className="relative aspect-[16/10] overflow-hidden">
      <img
        src={app.cover}
        alt={app.name}
        className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
        loading="lazy"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-white/95 via-white/30 to-transparent" />
      <div className="absolute top-4 left-4">
        <StatusBadge status={app.status} />
      </div>
      <div className="absolute top-4 right-4 text-[10px] uppercase tracking-[0.2em] text-stone-700 bg-white/80 backdrop-blur px-2 py-1">
        {app.year}
      </div>
    </div>

    <div className="p-7 flex-1 flex flex-col">
      <div className="text-xs uppercase tracking-[0.2em] text-[#B8860B] mb-2">{app.category}</div>
      <h3 className="font-display text-2xl text-stone-900 mb-2 tracking-tight">{app.name}</h3>
      <p className="text-stone-600 font-medium text-sm mb-3">{app.tagline}</p>
      <p className="text-stone-500 text-sm leading-relaxed flex-1">{app.description}</p>

      <div className="flex flex-wrap gap-1.5 mt-5 mb-6">
        {app.stack.map((t) => (
          <span key={t} className="text-[10px] font-mono uppercase tracking-wider text-stone-600 border border-stone-200 px-2 py-1">
            {t}
          </span>
        ))}
      </div>

      <a
        href={app.url}
        target="_blank"
        rel="noreferrer"
        data-testid={`app-card-cta-${app.id}`}
        className="inline-flex items-center justify-between gap-2 border border-stone-300 px-4 py-3 text-sm font-medium text-stone-900 hover:bg-stone-900 hover:text-white hover:border-stone-900 transition-all"
      >
        <span>Visit live app</span>
        <ExternalLink className="w-4 h-4" />
      </a>
    </div>
  </Reveal>
);

const Apps = () => {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("All");

  useEffect(() => window.scrollTo({ top: 0 }), []);

  const categories = useMemo(
    () => ["All", ...Array.from(new Set(APPS.map((a) => a.category)))],
    []
  );

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return APPS.filter((a) => {
      const matchesCat = category === "All" || a.category === category;
      const matchesQ =
        !q ||
        a.name.toLowerCase().includes(q) ||
        a.description.toLowerCase().includes(q) ||
        a.tagline.toLowerCase().includes(q) ||
        a.stack.some((s) => s.toLowerCase().includes(q));
      return matchesCat && matchesQ;
    });
  }, [query, category]);

  return (
    <main data-testid="page-apps">
      <section className="relative pt-40 pb-20 overflow-hidden border-b border-stone-200">
        <div className="absolute inset-0 bg-grid opacity-30 pointer-events-none" />
        <div className="absolute -top-32 right-10 w-[600px] h-[400px] glow-amber pointer-events-none" />
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 relative">
          <Reveal>
            <div className="inline-flex items-center gap-2 mb-8 border border-stone-300 px-3 py-1.5 backdrop-blur-md">
              <Sparkles className="w-3.5 h-3.5 text-[#B8860B]" />
              <span className="text-xs uppercase tracking-[0.25em] text-stone-700">
                Built with Emergent · Shipped by ITHR Technologies
              </span>
            </div>
            <h1 className="font-display text-stone-900 text-5xl md:text-7xl lg:text-8xl font-light tracking-tighter mb-8 max-w-5xl">
              Real apps,<br />
              <span className="italic text-[#B8860B]">shipped to production.</span>
            </h1>
            <p className="text-stone-600 text-lg md:text-xl max-w-2xl leading-relaxed">
              A live showcase of full-stack applications designed, built and deployed by our team — each one engineered to solve a real problem for a real client, end to end.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Filter bar */}
      <section className="sticky top-20 z-30 bg-[#FAF7F2]/85 backdrop-blur-xl border-b border-stone-200">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-5 flex flex-col md:flex-row gap-4 md:items-center md:justify-between">
          <div className="flex flex-wrap gap-2" data-testid="apps-category-filters">
            {categories.map((c) => (
              <button
                key={c}
                data-testid={`apps-filter-${c.replaceAll(" ", "-").toLowerCase()}`}
                onClick={() => setCategory(c)}
                className={`px-3.5 py-1.5 text-xs uppercase tracking-[0.18em] border transition-colors ${
                  category === c
                    ? "bg-stone-900 text-white border-stone-900"
                    : "border-stone-300 text-stone-700 hover:border-stone-500"
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          <div className="relative md:max-w-xs w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
            <input
              data-testid="apps-search"
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search apps, stack, problem…"
              className="form-input pl-10"
            />
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="py-16 md:py-20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="mb-8 text-sm text-stone-500">
            Showing <span className="text-stone-900 font-medium">{visible.length}</span> of {APPS.length} apps
          </div>

          {visible.length === 0 ? (
            <div className="border border-dashed border-stone-300 p-16 text-center" data-testid="apps-empty-state">
              <p className="text-stone-600 mb-4">No apps match your filters.</p>
              <button
                onClick={() => {
                  setQuery("");
                  setCategory("All");
                }}
                className="text-[#B8860B] link-underline"
              >
                Clear filters
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {visible.map((app, i) => (
                <AppCard key={app.id} app={app} index={i} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-20 md:py-28 border-t border-stone-200 bg-[#F5F1EA]">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10 text-center">
          <Reveal>
            <div className="text-xs uppercase tracking-[0.25em] text-[#B8860B] font-semibold mb-6">
              Your idea, deployed in weeks
            </div>
            <h2 className="font-display text-stone-900 text-4xl md:text-5xl lg:text-6xl font-light tracking-tighter mb-8">
              Got something you'd like<br />
              <span className="italic text-[#B8860B]">us to ship?</span>
            </h2>
            <p className="text-stone-600 text-lg max-w-2xl mx-auto mb-10 leading-relaxed">
              We turn briefs into production apps on Emergent — design, engineering, integrations and ops, end to end.
            </p>
            <Link to="/contact" data-testid="apps-bottom-cta" className="btn-primary">
              Start your project <ArrowUpRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default Apps;
